import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '@/api/client';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  const [moduleCredits, setModuleCredits] = useState({
    listening: 0,
    reading: 0,
    writing: 0,
    speaking: 0,
    mock: 0
  });
  const [showExactValidityDate, setShowExactValidityDate] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Fetch latest user data
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await authAPI.init();
      console.log('Init API response:', response.data);
      console.log('Module credits from API:', response.data.module_credits);
      
      setUser(response.data.user);
      setCredits(response.data.credit_balance);
      
      // Set show exact validity date flag
      setShowExactValidityDate(response.data.show_exact_validity_date || false);
      
      // Ensure module_credits exists and has proper values
      const moduleCreditsData = response.data.module_credits;
      if (moduleCreditsData) {
        console.log('Setting module credits to:', moduleCreditsData);
        setModuleCredits({
          listening: moduleCreditsData.listening || 0,
          reading: moduleCreditsData.reading || 0,
          writing: moduleCreditsData.writing || 0,
          speaking: moduleCreditsData.speaking || 0,
          mock: moduleCreditsData.mock || 0
        });
      }
      
      localStorage.setItem('user', JSON.stringify(response.data.user));
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      // Fetch credits
      await fetchUserData();
      
      return user; // Return user object with role
    } catch (error) {
      throw error; // Throw error to be caught in Login component
    }
  };

  const register = async (data) => {
    try {
      const response = await authAPI.register(data);
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      // Fetch credits
      await fetchUserData();
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setCredits(0);
  };

  const refreshCredits = async () => {
    try {
      const response = await authAPI.init();
      console.log('Credit refresh response:', response.data);
      console.log('Module credits from API:', response.data.module_credits);
      setCredits(response.data.credit_balance);
      
      // Ensure module_credits exists and has proper values
      const moduleCreditsData = response.data.module_credits;
      if (moduleCreditsData) {
        console.log('Setting module credits to:', moduleCreditsData);
        setModuleCredits({
          listening: moduleCreditsData.listening || 0,
          reading: moduleCreditsData.reading || 0,
          writing: moduleCreditsData.writing || 0,
          speaking: moduleCreditsData.speaking || 0,
          mock: moduleCreditsData.mock || 0
        });
      }
    } catch (error) {
      console.error('Failed to refresh credits:', error);
    }
  };

  const value = {
    user,
    loading,
    credits,
    moduleCredits,
    login,
    register,
    logout,
    refreshCredits,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isStaff: user?.role === 'staff' || user?.role === 'admin',
    isStudent: user?.role === 'student',
    showExactValidityDate,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
